import React from 'react';
import shieldLogo from '@/assets/logos/logo_transparent.png';

/**
 * The Vehix brand lockup: the shield emblem beside (or above) the word "Vehix".
 *
 * The primary lockup is the designed artwork in `vehix-emblem.png` (shield, VEHIX with the ™, and
 * the tagline). Use that wherever the emblem has room to render at 224px or more — below roughly
 * that size the tagline and the ™ stop being legible.
 *
 * This component is the text-based alternative for tight placements: it pairs the bare shield with
 * the name as live text, so the wordmark stays crisp at any size and remains editable in code. The
 * sidebar and mobile header currently compose the same pairing inline; this is here for new
 * placements that need it.
 *
 * `logo_transparent.png` is the shield on its own — it carries no text of any kind.
 */
export type VehixWordmarkOrientation = 'stacked' | 'inline';

interface VehixWordmarkProps {
  /** 'stacked' puts the word under the shield (auth screens); 'inline' sets it beside (nav). */
  orientation?: VehixWordmarkOrientation;
  /** Tailwind sizing for the shield, e.g. 'h-24 w-24'. */
  shieldClassName?: string;
  /** Tailwind type styles for the word, e.g. 'text-3xl font-bold'. */
  textClassName?: string;
  className?: string;
}

const layout: Record<VehixWordmarkOrientation, string> = {
  stacked: 'flex flex-col items-center gap-3',
  inline: 'flex flex-row items-center gap-2'
};

export const VehixWordmark: React.FC<VehixWordmarkProps> = ({
  orientation = 'stacked',
  shieldClassName = 'h-24 w-24',
  textClassName = 'text-3xl font-bold tracking-tight',
  className = ''
}) => (
  <div className={`${layout[orientation]} ${className}`.trim()}>
    <img
      src={shieldLogo}
      alt=''
      aria-hidden='true'
      className={`${shieldClassName} object-contain`}
    />
    <span className={textClassName}>Vehix</span>
  </div>
);

/**
 * The word "Vehix" with the trademark symbol set as a proper superscript.
 *
 * The symbol is sized down and given a negative right margin so it contributes
 * no width: a centred container would otherwise centre "Vehix" plus the symbol,
 * which pushes the word itself off centre. Left inline at full size it also
 * renders as tall as the name.
 */
export const VehixName: React.FC<{ className?: string }> = ({ className }) => (
  <span className={className}>
    Vehix
    <span className='align-super text-[0.5em] -mr-[0.5em]'>&trade;</span>
  </span>
);

export default VehixWordmark;
